import { DownloadIcon } from '@heroicons/react/outline';
import { FileDownload } from '@mui/icons-material';
import { Box, Button, List, ListItem } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import Carousel from '../richtText/Carousel';
import {
  getDisplayFileName,
  isImageFile,
  isPdfFile,
  isSpreadsheetFile,
  isVideoFile,
  isWordFile,
} from '../../../shared/files/fileType';
import { handleDownloadPdf } from './downloadPdf';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';

function ActivityAttachment({ fileUrls }: { fileUrls: string[] }) {
  const items = fileUrls.map((fileUrl) => ({ fileUrl }));

  const images = items.filter((item) => isImageFile(item.fileUrl));
  const pdfs = items.filter((item) => isPdfFile(item.fileUrl));
  const videos = items.filter((item) => isVideoFile(item.fileUrl));
  const wordDocuments = items.filter((item) => isWordFile(item.fileUrl));
  const excelFiles = items.filter((item) => isSpreadsheetFile(item.fileUrl));
  const { t } = useTranslation();

  if (!items.length) {
    return null;
  }

  return (
    <div className="flex-col space-y-3">
      {/* Video */}
      {videos.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            padding: '1rem',
            width: '100%',
          }}
        >
          {/* `src` on the element rather than a <source type="video/mp4">:
              the hard-coded MIME type made the browser refuse .mov/.webm
              uploads that isVideoFile now recognises. Letting the browser
              sniff the response Content-Type keeps every format playable.

              Sizing matches the image branch below. The element previously
              carried only `m-auto`, so with no width it fell back to the
              browser default (300px) before metadata loaded and to the
              video's intrinsic size after — either way far narrower than
              the post column, and much smaller than an image in the same
              post. `maxHeight` keeps a tall portrait clip from filling the
              viewport, and `object-fit: contain` preserves aspect ratio
              rather than stretching to that cap. */}
          <video
            controls
            src={videos[0].fileUrl}
            style={{
              width: '100%',
              maxWidth: '100%',
              height: 'auto',
              maxHeight: '70vh',
              objectFit: 'contain',
              display: 'block',
              borderRadius: 8,
            }}
          />
        </Box>
      ) : null}

      {/* Show single image — flow at the post column's natural width.
          Previous wrapper had `width: { sx: '100%', lg: '30rem' }` which
          (a) used `sx` as a breakpoint key — Joy treats it as unknown and
          ignores, so the rule never applied below `lg`, and (b) capped at
          30rem (480px) on `lg+` screens, letterboxing the image inside a
          much wider post column. Use 100% width with auto height; the
          Zoom wrapper still gives click-to-fullscreen for inspection. */}
      {images.length === 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            padding: '1rem',
            width: '100%',
          }}
        >
          <Zoom>
            <img
              src={images[0].fileUrl}
              alt="Afbeelding bij post"
              style={{
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: 8,
              }}
            />
          </Zoom>
        </Box>
      )}

      {/* Multiple images in carousel */}
      {images.length > 1 && (
        <Carousel sources={images.map((image) => image.fileUrl)} />
      )}

      {/* PDFs */}
      <List>
        {pdfs.map((pdf) => {
          const filename = getDisplayFileName(pdf.fileUrl);
          return (
            <ListItem sx={{ padding: 0 }} key={pdf.fileUrl}>
              <Button
                variant="outlined"
                startDecorator={<FileDownload />}
                onClick={() => handleDownloadPdf(pdf.fileUrl, filename)}
              >
                {filename}
              </Button>
            </ListItem>
          );
        })}

        {/* Word documents */}
        {wordDocuments.map((doc) => {
          return (
            <li key={doc.fileUrl} className="flex-col space-y-3">
              <a
                className="flex justify-start items-center space-x-3 border-sky border rounded-md p-2 text-sky hover:border-sky-dark hover:text-sky-dark"
                href={doc.fileUrl}
              >
                <DownloadIcon className="w-4 text-sky" />
                <p>Word document</p>
              </a>
            </li>
          );
        })}

        {/* Excel files */}
        {excelFiles.map((doc) => {
          const filename = getDisplayFileName(doc.fileUrl);
          return (
            <li key={doc.fileUrl} className="flex-col space-y-3">
              <a
                className="flex justify-start items-center space-x-3 border-sky border rounded-md p-2 text-sky hover:border-sky-dark hover:text-sky-dark"
                href={doc.fileUrl}
              >
                <DownloadIcon className="w-4 text-sky" />
                <p>{filename.slice(0, 28)}</p>
              </a>
            </li>
          );
        })}
      </List>
    </div>
  );
}

export default ActivityAttachment;
